/**
 * Speaking — local grading by recordings, optional async AI grading via content API.
 */

import type {
    ContentGrader,
    GradingContext,
    GradingResult,
    ScoringConfig,
    QuestionCounter,
} from '@ai-content-hub/content-core';
import { runGradingSafely } from '@ai-content-hub/content-core';
import {
    contentService,
    generateService,
    normalizePronunciationScore,
    speakingScoreService,
    type SpeakingScoreResult,
} from '@ai-content-hub/shared-api';
import { tiptapJsonToPlainText, isTiptapDocument } from '@ai-content-hub/content-kit';
import type { SpeakingData, SpeakingItem, SpeakingScoringConfig } from './schema';
import {
    DEFAULT_SPEAKING_GRADING_QUESTION_TYPE,
    SPEAKING_READ_ALOUD_QUESTION_TYPE,
    SPEAKING_GRADING_QUESTION_TYPES,
    SPEAKING_QUESTION_TYPES_WITH_PICTURE,
} from './schema';
import {
    extractTextFromSpeakingPage,
    dataUrlToBlob,
    picturePayloadForSpeakingGrade,
} from './utils';
import map from 'lodash/map';
import trim from 'lodash/trim';

/** One prompt's SpeechAce result, as stored in `GradingResult.details` (EAA-1890). */
export interface SpeakingPronunciationEntry {
    itemId: string;
    /** The script the recording was scored against — the panel renders exactly this. */
    referenceText: string;
    /** Raw agent result; `normalizePronunciationScore` turns it into words + overall. */
    result: Record<string, unknown>;
}

export interface SpeakingItemResult {
    itemId: string;
    isCorrect: boolean;
    score: number;
    min: number;
    max: number;
}

// EAA-1857: single source of truth — the editor's grading-type dropdown and this
// validation list must not drift apart.
const VALID_AI_QUESTION_TYPES = SPEAKING_GRADING_QUESTION_TYPES.map((q) => q.code);

/**
 * ELMS-2377: grade-jobs already created by a deferred (host FORCE_SUBMIT) submit, keyed by
 * content + prompt + recording fingerprint, so a repeat FORCE_SUBMIT after the first flight
 * finished never starts a second billable job for the SAME recording. Lives as long as the
 * iframe page; a re-recorded prompt (different fingerprint) or a failed start creates a new job.
 */
const deferredSpeakingJobs = new Map<string, { job_id: string; draft?: boolean }>();

/** Test hook. */
export function resetDeferredSpeakingJobs(): void {
    deferredSpeakingJobs.clear();
}

function recordingFingerprint(dataUrl: string): string {
    let h = 0x811c9dc5;
    for (let i = 0; i < dataUrl.length; i += 1) {
        h ^= dataUrl.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return `${dataUrl.length}:${(h >>> 0).toString(16)}`;
}

function isRecordAudioAnswered(raw: unknown): boolean {
    return typeof raw === 'string' && trim(raw) !== '';
}

function guessFileExtension(mime: string): string {
    const m = mime.toLowerCase();
    if (m.includes('webm')) return 'webm';
    if (m.includes('wav')) return 'wav';
    if (m.includes('mp4')) return 'mp4';
    if (m.includes('mpeg') || m.includes('mp3')) return 'mp3';
    if (m.includes('ogg')) return 'ogg';
    if (m.includes('aac')) return 'aac';
    if (m.includes('flac')) return 'flac';
    return 'webm';
}

function extractOverallPercentage(api: Record<string, unknown>): number {
    const nested = api.data;
    const nestedRec =
        nested && typeof nested === 'object' && !Array.isArray(nested)
            ? (nested as Record<string, unknown>)
            : null;

    const candidates: unknown[] = [
        api.overall_score,
        nestedRec?.overall_score,
        api.overall_score_percentage,
        api.score_percentage,
        api.total_score_percentage,
        nestedRec?.overall_score_percentage,
    ];
    for (const v of candidates) {
        if (typeof v === 'number' && Number.isFinite(v)) {
            return v;
        }
    }

    // EAA-1857: a Speechace result carries no `overall_score` — it reports
    // `pronunciation`/`score` plus PTE/IELTS/CEFR sub-scores under
    // `details.text_score`. Reading only the flat fields above scored every
    // Speechace-graded prompt as 0. Defer to the shared normaliser, which also
    // applies the engine scale rule (ELSA 0-5 ×20, Speechace already 0-100).
    return normalizePronunciationScore((nestedRec ?? api) as unknown as SpeakingScoreResult)
        .overall;
}

/** When `gradingQuestionType` unset/invalid: ELMS-2369: free-talk default QT_SPEAK_RESPOND_SITUATION; Read Aloud only when chosen explicitly. */
function resolveQuestionType(scoring: SpeakingScoringConfig | undefined): string {
    const raw = scoring?.gradingQuestionType?.trim();
    if (raw && (VALID_AI_QUESTION_TYPES as readonly string[]).includes(raw)) {
        return raw;
    }
    return DEFAULT_SPEAKING_GRADING_QUESTION_TYPE;
}

function resolveLanguage(ctx: GradingContext | undefined): 'vi' | 'en' {
    const fromCfg = ctx?.config?.i18n?.defaultLanguage;
    if (fromCfg === 'vi' || fromCfg === 'en') {
        return fromCfg;
    }
    return 'en';
}

/**
 * ELMS-2369: speaking is graded like essay - every recorded `record-audio` prompt goes to AI.
 * `item.data.autoGrading` (legacy checkbox) no longer gates anything; `aiGradingEnabled`
 * stays honoured only so importer-built content with non-record-audio items keeps working.
 */
function itemUsesAiGrading(item: SpeakingItem, scoring?: SpeakingScoringConfig): boolean {
    return item.type === 'record-audio' || scoring?.aiGradingEnabled === true;
}

function wantsSpeakingAiPath(data: SpeakingData, scoring?: SpeakingScoringConfig): boolean {
    if (scoring?.aiGradingEnabled === true) {
        return true;
    }
    return (data.items ?? []).some((i) => itemUsesAiGrading(i, scoring));
}

export function validateSpeakingAnswer(data: SpeakingData, answer: unknown): boolean {
    if (!answer || typeof answer !== 'object' || Array.isArray(answer)) {
        return false;
    }
    const rec = answer as Record<string, unknown>;
    const items = data.items ?? [];
    if (items.length === 0) {
        return false;
    }
    return items.some((item) => isRecordAudioAnswered(rec[item.id]));
}

/** Local score only (recorded vs empty): base `details` for the AI paths and the nothing-recorded case. */
export function gradeSpeaking(
    data: SpeakingData,
    answers: Record<string, unknown>,
    scoring?: SpeakingScoringConfig & ScoringConfig
): GradingResult {
    const items = data.items ?? [];
    const scoringConfig = scoring as SpeakingScoringConfig | undefined;
    const pointsBase =
        scoringConfig?.pointsPerQuestion ?? (scoring as { max?: number } | undefined)?.max ?? 1;
    const passingThreshold =
        scoringConfig?.passingThreshold ??
        (scoring as { passingScore?: number } | undefined)?.passingScore ??
        0.7;
    const partialCredit = scoring?.partialCreditEnabled ?? true;

    const itemResults: SpeakingItemResult[] = map(items, (item: SpeakingItem) => {
        const raw = answers[item.id];
        const itemMax = item.points ?? pointsBase;
        const answered = isRecordAudioAnswered(raw);
        const score = answered ? itemMax : 0;
        return {
            itemId: item.id,
            isCorrect: answered,
            score,
            min: 0,
            max: itemMax,
        };
    });

    const maxPoints = itemResults.reduce((s, r) => s + r.max, 0);
    const totalScore = itemResults.reduce((s, r) => s + r.score, 0);
    const ratio = maxPoints > 0 ? totalScore / maxPoints : 0;
    const passed = ratio >= passingThreshold;
    const recordedCount = itemResults.filter((r) => r.isCorrect).length;

    return {
        isCorrect: passed,
        score: totalScore,
        max: maxPoints,
        details: {
            items: itemResults,
            passed,
            ratio,
            partialCredit,
            recordedCount,
            questionCount: itemResults.length,
        },
    };
}

/**
 * The words the learner was asked to say, as plain text.
 *
 * EAA-1890: pulled out of `gradeSpeakingWithAi` because Read Aloud now needs the same
 * string as the SpeechAce *reference script*, not just as AI prompt context — the two
 * must be the identical text or the per-word alignment in the result panel drifts from
 * the passage the player renders.
 */
export function speakingPromptText(item: SpeakingItem): string {
    const titleVal = item.data?.title;
    if (titleVal && typeof titleVal === 'object' && isTiptapDocument(titleVal)) {
        return tiptapJsonToPlainText(titleVal).trim();
    }
    return trim(typeof titleVal === 'string' ? titleVal : '');
}

/**
 * EAA-1890 (BA review, row "Loại chấm Read Aloud — chấm theo API từng từ của SpeechAce").
 *
 * Read Aloud is the one grading type whose task is a known script, so it is graded by
 * the SpeechAce endpoint interactive-reading already uses
 * (`speakingScoreService.score` → `POST /content/speaking-score/generate`) instead of
 * the criteria-based `/api/v3/scoring/speaking` job. That endpoint returns per-word and
 * per-phone quality, which is the whole point: an author who picks Read Aloud wants to
 * see WHICH words were mispronounced, and the criteria API can only return an overall
 * band plus prose.
 *
 * Two consequences worth knowing:
 *  - no `user_id` is required, so Read Aloud grades in hosts that never pass one;
 *  - the reference script is the prompt title (falling back to the lesson page text),
 *    so an empty prompt has nothing to score against and falls back to the local score.
 */
async function gradeSpeakingReadAloud(
    data: SpeakingData,
    answers: Record<string, unknown>,
    scoring: SpeakingScoringConfig | undefined,
    context: GradingContext
): Promise<GradingResult> {
    const scoringConfig = scoring ?? {};
    const passingThreshold =
        scoringConfig.passingThreshold ??
        (scoring as { passingScore?: number } | undefined)?.passingScore ??
        0.7;
    const pageText = extractTextFromSpeakingPage(data.pageContent);

    const pronunciationResults: SpeakingPronunciationEntry[] = [];
    const perItemScores: number[] = [];

    for (const item of data.items ?? []) {
        const raw = answers[item.id];
        if (!isRecordAudioAnswered(raw)) {
            continue;
        }
        if (!itemUsesAiGrading(item, scoringConfig)) {
            continue;
        }
        const referenceText = speakingPromptText(item) || pageText;
        if (!referenceText) {
            // ELMS-2369: nothing to score against must NOT fall back to the full-credit local
            // score - an unverifiable recording earns 0.
            perItemScores.push(0);
            continue;
        }

        const result = (await speakingScoreService.score(
            {
                text: referenceText,
                audio: dataUrlToBlob(raw as string),
                noMc: referenceText.includes('\n') ? 1 : 0,
                // EAA-2117: the service drops a non-UUID (unsaved draft) id; the tenant
                // then comes from the host so SpeechAce still runs in the `/new` preview.
                contentId: context.contentId,
                tenantId: context.tenantId,
            },
            context.sessionToken
        )) as unknown as Record<string, unknown>;

        pronunciationResults.push({ itemId: item.id, referenceText, result });
        perItemScores.push(normalizePronunciationScore(result, passingThreshold).overall);
    }

    if (perItemScores.length === 0) {
        return gradeSpeaking(data, answers, scoring);
    }

    const avgPct = perItemScores.reduce((a, b) => a + b, 0) / perItemScores.length;
    const local = gradeSpeaking(data, answers, scoring);

    return {
        isCorrect: avgPct >= passingThreshold * 100,
        score: avgPct,
        max: 100,
        details: {
            ...((local.details ?? {}) as Record<string, unknown>),
            pronunciationGrading: true,
            questionType: SPEAKING_READ_ALOUD_QUESTION_TYPE,
            pronunciationResults,
        },
    };
}

async function gradeSpeakingWithAi(
    data: SpeakingData,
    answers: Record<string, unknown>,
    scoring: SpeakingScoringConfig | undefined,
    context: GradingContext
): Promise<GradingResult> {
    const contentId = context.contentId;
    if (!contentId) {
        throw new Error('Speaking AI grading requires contentId in grading context');
    }

    const scoringConfig = scoring ?? {};
    const uid = trim(context.userId ?? '') || trim(scoringConfig.userId ?? '');
    // ELMS-2369: inside a content session (session token) the BE takes the learner from the
    // JWT-verified session row, so a missing userId is fine there (the LMS play URL carries none).
    // Never derive it from untrusted input. Outside a session it is still required.
    if (!uid && !context.sessionToken) {
        throw new Error(
            'Speaking AI grading requires userId in config.scoring, ContentPlayer userId prop, or embed ?userId='
        );
    }

    const questionType = resolveQuestionType(scoringConfig);
    const language = resolveLanguage(context);
    const pageText = extractTextFromSpeakingPage(data.pageContent);
    const pictureFields = (SPEAKING_QUESTION_TYPES_WITH_PICTURE as readonly string[]).includes(
        questionType
    )
        ? picturePayloadForSpeakingGrade(data.pageContent)
        : {};

    const items = data.items ?? [];
    const apiResponses: Record<string, unknown>[] = [];
    const perItemScores: number[] = [];

    const isGradableItem = (item: SpeakingItem): boolean =>
        isRecordAudioAnswered(answers[item.id]) && itemUsesAiGrading(item, scoringConfig);

    // Start a background grading job for one recorded prompt (no waiting).
    // EAA-2117: saved content keeps `/content/<id>/speaking/grade-job`; an unsaved draft
    // (question-bank `/new` preview) goes to `/content/draft/grade-job` as the same
    // multipart plus `content_type=speaking` and `tenant_id`.
    const startItemJob = (item: SpeakingItem) => {
        const dataUrl = answers[item.id] as string;
        const blob = dataUrlToBlob(dataUrl);
        const ext = guessFileExtension(blob.type);
        const filename = `speaking-${item.id}.${ext}`;
        const file = new File([blob], filename, { type: blob.type });

        const promptTitle = speakingPromptText(item);
        const questionContent = [pageText, promptTitle].filter(Boolean).join('\n\n').trim();

        return contentService.startSpeakingGradeJob(
            contentId,
            {
                question_type: questionType,
                question_content: questionContent || promptTitle || pageText || 'Speaking task',
                audio_file: file,
                ...(uid ? { user_id: uid } : {}),
                language,
                ...pictureFields,
            },
            { tenantId: context.tenantId, sessionToken: context.sessionToken }
        );
    };

    // ELMS-2377: host FORCE_SUBMIT — only make sure every recorded prompt's job EXISTS on the
    // server (jobs carry the session id; the backend stamps the averaged score on the session
    // once every job is done), creating them in parallel, and do not poll. Any failed start falls
    // back to the synchronous path below, reusing the jobs that did start (no duplicate AI cost).
    const preStarted = new Map<string, { job_id: string; draft?: boolean }>();
    if (context.deferAiResult && context.sessionToken) {
        const gradable = items.filter(isGradableItem);
        const memoKeyOf = (item: SpeakingItem) =>
            `${contentId}\u0000${item.id}\u0000${recordingFingerprint(answers[item.id] as string)}`;
        const settled = await Promise.allSettled(
            gradable.map((item) => {
                const known = deferredSpeakingJobs.get(memoKeyOf(item));
                if (known) {
                    return Promise.resolve(known);
                }
                // `.then` wrapper: a corrupt data URL throws synchronously inside
                // startItemJob; it must reject THIS item only, never abort the others.
                return Promise.resolve()
                    .then(() => startItemJob(item))
                    .then((job) => {
                        if (!job.draft) {
                            deferredSpeakingJobs.set(memoKeyOf(item), job);
                        }
                        return job;
                    });
            })
        );
        settled.forEach((res, idx) => {
            if (res.status === 'fulfilled') {
                preStarted.set(gradable[idx].id, res.value);
            } else {
                console.warn('[speaking] deferred grade-job start failed, falling back to waiting', res.reason);
            }
        });
        if (
            gradable.length > 0 &&
            preStarted.size === gradable.length &&
            [...preStarted.values()].every((j) => !j.draft)
        ) {
            return { isCorrect: false, score: 0, max: 100, pending: true };
        }
    }

    for (const item of items) {
        if (!isGradableItem(item)) {
            continue;
        }
        const { job_id, draft } = preStarted.get(item.id) ?? (await startItemJob(item));

        const finalJob = await generateService.pollJobStatus(
            job_id,
            () => {
                // onStatusChange — no-op; we just wait for terminal state
            },
            {
                interval: 2000,
                maxAttempts: 90,
                pendingTimeoutMs: 30000, // 2s × 90 = 180s max, 30s pending limit
                // ELMS-2365: student view-token lacks job.read; poll with Session auth (not for drafts)
                ...(!draft && context.sessionToken ? { sessionToken: context.sessionToken } : {}),
            }
        );

        if (finalJob.status === 'failed') {
            throw new Error(finalJob.error || `Speaking grading job failed for item ${item.id}`);
        }

        const apiResult = (finalJob.result ?? {}) as Record<string, unknown>;
        apiResponses.push({ itemId: item.id, result: apiResult });
        perItemScores.push(extractOverallPercentage(apiResult));
    }

    if (perItemScores.length === 0) {
        return gradeSpeaking(data, answers, scoring);
    }

    // ELMS-2369: the average is over RECORDED prompts only (unrecorded ones are skipped, not 0).
    // Intentional and unchanged: it matches the ELMS-2365 BE write-back
    // (`apply_speaking_grade_scores` averages the same recorded set) so both paths agree.
    const avgPct = perItemScores.reduce((a, b) => a + b, 0) / Math.max(perItemScores.length, 1);
    const passing =
        scoringConfig.passingThreshold ??
        (scoring as { passingScore?: number } | undefined)?.passingScore ??
        0.7;
    const isCorrect = avgPct >= passing * 100;

    const local = gradeSpeaking(data, answers, scoring);

    return {
        isCorrect,
        score: avgPct,
        max: 100,
        details: {
            ...((local.details ?? {}) as Record<string, unknown>),
            aiGrading: true,
            questionType,
            apiResponses,
        },
    };
}

export const speakingContentGrader: ContentGrader<SpeakingData, Record<string, unknown>> = {
    grade: (data, answer, cfg, ctx) => {
        const scoring = cfg as SpeakingScoringConfig | undefined;
        if (wantsSpeakingAiPath(data, scoring)) {
            // ELMS-2369: like essay, a missing contentId throws (inside the AI graders) instead of
            // silently falling back to the full-credit local score.
            // EAA-1890: Read Aloud goes to the per-word SpeechAce endpoint; every other
            // QT_SPEAK_* type stays on the criteria-based grading job.
            if (resolveQuestionType(scoring) === SPEAKING_READ_ALOUD_QUESTION_TYPE) {
                return runGradingSafely('speaking-read-aloud', () =>
                    gradeSpeakingReadAloud(data, answer ?? {}, scoring, ctx ?? ({} as GradingContext))
                );
            }
            // ELMS-2369: any failure (AG down / 403 / timeout / failed job) is logged sanitised and
            // surfaces to the learner as a friendly i18n message; it still earns no score.
            return runGradingSafely('speaking-ai', () =>
                gradeSpeakingWithAi(data, answer ?? {}, scoring, ctx ?? ({} as GradingContext))
            );
        }
        return gradeSpeaking(data, answer ?? {}, scoring);
    },
    requiresNetwork: (data, cfg) => wantsSpeakingAiPath(data, cfg as SpeakingScoringConfig | undefined),
    validateAnswer: (answer, content) => validateSpeakingAnswer(content, answer),
};

/**
 * Each speaking item (recording prompt) is 1 gradable question.
 */
export function countSpeakingQuestions(data: SpeakingData): number {
    return (data.items ?? []).length;
}

export const speakingQuestionCounter: QuestionCounter<SpeakingData> = {
    countQuestions: countSpeakingQuestions,
};
